import React from 'react';
import { CaretRightFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useOrderNote } from '../../hooks/useOrderNote';
import { OrderNoteType } from '../../pages/delivery/graphql/types/DeliveryTaskOrderType';

interface OrderNoteItemProps {
    orderNote: OrderNoteType | null | undefined;
    currentLanguage: string;
    className?: string;
}

const OrderNoteItem: React.FC<OrderNoteItemProps> = ({ orderNote, currentLanguage, className = '' }) => {
    const { t } = useTranslation();
    const {
        noteContent,
        isNoteExpanded,
        isNoteTruncated,
        handleToggleNote,
        noteWrapperCollapsedRef,
        noteTextCollapsedRef,
    } = useOrderNote({ orderNote, currentLanguage });

    if (!noteContent) {
        return null;
    }

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={handleToggleNote}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleToggleNote();
                }
            }}
            className={`flex justify-between items-start w-full cursor-pointer py-2 outline-none focus-visible:outline-2 focus-visible:outline-[#6BA8FF] focus-visible:outline-offset-2 focus-visible:rounded-md gap-3 ${className}`}
            aria-expanded={isNoteExpanded}
        >
            <span className="text-gray-600 text-sm">
                {t('pages.delivery.orderDetail.note')}:
            </span>
            <div
                className={isNoteExpanded
                    ? "flex flex-col items-end gap-1 min-w-0 flex-1 w-full text-right"
                    : "flex items-center justify-end gap-1.5 min-w-0 flex-1 w-full text-right"}
                ref={isNoteExpanded ? undefined : noteWrapperCollapsedRef}
            >
                <span
                    className={isNoteExpanded
                        ? "text-sm font-semibold text-gray-800 whitespace-pre-wrap break-words text-right max-w-full"
                        : "text-sm font-semibold text-gray-800 whitespace-nowrap overflow-hidden text-ellipsis max-w-full"}
                    ref={isNoteExpanded ? undefined : noteTextCollapsedRef}
                >
                    {noteContent}
                </span>
                {!isNoteExpanded && isNoteTruncated && (
                    <CaretRightFilled className="text-xs text-blue-500 font-semibold tracking-wide" />
                )}
            </div>
        </div>
    );
};

export default OrderNoteItem;

